// The Claude support of the Brewcore API (app-v1 http-claude-*). Dominik, 19.10.2026: "der claude schluessel soll pro
// account eingestellt werden können uns bei uns gespeichert werden": the key is kept by the cloud per account (sealed),
// the API asks Anthropic with it; this browser never talks to Anthropic and never sees the key again after it was
// typed. Suggestion, chat and the grinder and bean lookups block until Claude answered.
import type {
  ClaudeAccountResponse,
  ClaudeBeanResponse,
  ClaudeGrinderResponse,
  ClaudeSettingsRequest,
  ClaudeThreadResponse,
  Lang,
} from '@brewcore/app-contract';
import { jsonRequest, type FetchLike, type HttpResult, type HttpTarget } from '../core/http.js';

export type { FetchLike, HttpResult } from '../core/http.js';

export class ClaudeApi {
  private readonly target: HttpTarget;

  constructor(
    fetchImpl: FetchLike = (input, init) => fetch(input, init),
    base = '',
    headers: Record<string, string> = {},
  ) {
    this.target = { fetchImpl, base, headers };
  }

  account(): Promise<HttpResult<ClaudeAccountResponse>> {
    return jsonRequest(this.target, 'GET', '/api/account/claude', null, 'http-claude-account');
  }

  /** model, effort, asking after a sour or bitter rating, the dismissed setup line: they follow the account */
  settings(patch: ClaudeSettingsRequest): Promise<HttpResult<ClaudeAccountResponse>> {
    return jsonRequest(this.target, 'PATCH', '/api/account/claude', patch, 'http-claude-account');
  }

  /** Checked once by the API with Anthropic, then kept sealed in the account; never sent back. */
  setKey(key: string): Promise<HttpResult<ClaudeAccountResponse>> {
    return jsonRequest(this.target, 'PUT', '/api/account/claude/key', { key }, 'http-claude-account');
  }

  deleteKey(): Promise<HttpResult<ClaudeAccountResponse>> {
    return jsonRequest(this.target, 'DELETE', '/api/account/claude/key', null, 'http-claude-account');
  }

  grinder(machine: string): Promise<HttpResult<ClaudeGrinderResponse>> {
    return jsonRequest(
      this.target,
      'GET',
      `/api/machines/${machine}/claude/grinder`,
      null,
      'http-claude-grinder',
    );
  }

  lookUpGrinder(machine: string, again: boolean, lang: Lang): Promise<HttpResult<ClaudeGrinderResponse>> {
    return jsonRequest(
      this.target,
      'POST',
      `/api/machines/${machine}/claude/grinder`,
      { again, lang },
      'http-claude-grinder',
    );
  }

  /** What Claude found out about a bean for this account (board "Eine Bohne", 25.10.2026). */
  bean(machine: string, bean: string): Promise<HttpResult<ClaudeBeanResponse>> {
    return jsonRequest(
      this.target,
      'GET',
      `/api/machines/${machine}/beans/${bean}/claude`,
      null,
      'http-claude-bean',
    );
  }

  lookUpBean(
    machine: string,
    bean: string,
    again: boolean,
    lang: Lang,
  ): Promise<HttpResult<ClaudeBeanResponse>> {
    return jsonRequest(
      this.target,
      'POST',
      `/api/machines/${machine}/beans/${bean}/claude`,
      { again, lang },
      'http-claude-bean',
    );
  }

  thread(machine: string, shot: string): Promise<HttpResult<ClaudeThreadResponse>> {
    return jsonRequest(
      this.target,
      'GET',
      `/api/machines/${machine}/shots/${shot}/claude`,
      null,
      'http-claude-thread',
    );
  }

  suggest(machine: string, shot: string, lang: Lang): Promise<HttpResult<ClaudeThreadResponse>> {
    return jsonRequest(
      this.target,
      'POST',
      `/api/machines/${machine}/shots/${shot}/claude/suggest`,
      { lang },
      'http-claude-thread',
    );
  }

  chat(machine: string, shot: string, text: string, lang: Lang): Promise<HttpResult<ClaudeThreadResponse>> {
    return jsonRequest(
      this.target,
      'POST',
      `/api/machines/${machine}/shots/${shot}/claude/chat`,
      { text, lang },
      'http-claude-thread',
    );
  }

  /** adopted (its set command done), or offered again after "Rückgängig" (applied false) */
  applied(
    machine: string,
    shot: string,
    index: number,
    applied = true,
  ): Promise<HttpResult<ClaudeThreadResponse>> {
    return jsonRequest(
      this.target,
      'POST',
      `/api/machines/${machine}/shots/${shot}/claude/applied`,
      applied ? { index } : { index, applied: false },
      'http-claude-thread',
    );
  }
}
